import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MAT_DIALOG_DATA,
  MatDialogRef,
} from '@angular/material/dialog';
import { InputField } from '../../../../shared/components/input-field/input-field';
import { DialogData } from '../../../../shared/models/dialog-data.model';
import {
  OriginVhostPHP,
  OriginVhostPHPConfigParameter,
  OriginVhostPHPModule,
  OriginVhostUpdate,
} from '../../models/origin-vhost.models';
import { Server } from '../../../../shared/models/server.models';

@Component({
  selector: 'app-php-settings-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    InputField,
  ],
  templateUrl: './php-settings-dialog.component.html',
  styleUrl: './php-settings-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhpSettingsDialogComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<PhpSettingsDialogComponent>);

  readonly data = inject<
    DialogData & {
      modules: OriginVhostPHPModule[];
      parameters: OriginVhostPHPConfigParameter[];
      php: OriginVhostPHP;
      server: Server;
      aliases: string[];
    }
  >(MAT_DIALOG_DATA);

  private fb = inject(FormBuilder);

  form = this.fb.nonNullable.group({
    modules: [[] as string[]],
    configs: this.fb.array([]),
    phpVersion: [''],
  });

  readonly ERROR_MESSAGES: Record<string, string> = {
    invalidMemory: 'Invalid memory value! Value should include suffix (k,M,G)',
  };

  get moduleOptions(): { label: string; value: string }[] {
    return this.data.modules.map((m) => ({ label: m.name, value: m.name }));
  }

  get configParams(): OriginVhostPHPConfigParameter[] {
    return this.data.parameters;
  }

  get configs(): FormArray {
    return this.form.get('configs') as FormArray;
  }

  get phpOptions(): { label: string; value: string }[] {
    return this.data.server.services
      .filter((service) => service.startsWith('php'))
      .map((s) => ({ label: s.slice(3), value: s.slice(3) }));
  }

  getConfigName(id: number): string {
    return this.configParams.find((c) => c.id === id)?.name || '';
  }

  addConfig(id: number, value: string | string[]) {
    this.configs.push(
      this.fb.group({
        parameter: [id],
        value: [value, id === 3 ? [] : [this.memoryValidator]],
      }),
    );
  }

  ngOnInit(): void {
    this.form.controls.phpVersion.setValue(this.data.php.version.toString());
    this.form.controls.modules.setValue(this.data.php.modules.map((m) => m.name));
    this.configParams.forEach((param) => {
      const currentValue =
        this.data.php.config.find((v) => v.parameter.id === param.id)?.value || '';

      this.addConfig(param.id, param.id === 3 ? currentValue.split(',') : currentValue);
    });
  }

  submit(): void {
    if (this.form.invalid) return;

    const value = this.form.getRawValue();

    const modules = value.modules.map(
      (name) => this.data.modules.find((module) => module.name === name)!.id,
    );

    const configs = (value.configs as { parameter: number; value: string | string[] }[])
      .map(({ parameter, value }) => ({
        parameter,
        value: Array.isArray(value) ? value.join(',') : value,
      }))
      .filter(({ value }) => !!value);

    const phpUpdate: OriginVhostUpdate = {
      aliases: this.data.aliases,
      php: { version: Number(value.phpVersion) },
    };

    const configChanged = this.form.controls.configs.dirty || this.form.controls.modules.dirty;

    const versionChanged = this.form.controls.phpVersion.dirty;

    this.dialogRef.close({
      ...(configChanged && { modules, configs }),
      ...(versionChanged && { phpUpdate }),
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  private memoryValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;
    const isValid = !!value.match(/^\d+(:?[k,M,G])$/);
    return isValid ? null : { invalidMemory: true };
  }
}
